import { FC, useMemo } from "react";
import styled from "@emotion/styled";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { Add, Remove } from "@mui/icons-material";
import { Icon, IconButton } from "@mui/material";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { isEmpty } from "lodash";

import { PCBuildRow } from "modules/pcbuilder/types";
import {
  colors,
  formatCurrency,
  formatCurrencySRP,
  productDetailsPageUrl,
} from "common";
import { FlexBox, FlexRow } from "components/flex-box";
import { H4, Span } from "components/Typography";
import LazyImage from "components/LazyImage";

import { useAppDispatch } from "hooks/use-app-dispatch";
import { useAppSelector } from "hooks/use-app-selector";
import { useIsMobile } from "hooks";
import { useRouter } from "next/router";

import modules from "modules";
import { BuildPartProperties } from "components";
import mapCategoryIcon from "./MapCategory";
import Link from "next/link";

interface Props {
  data?: PCBuildRow;
}

const {
  actions: pcBuilderActions,
  selectors: pcBuilderSelectors,
  utils: pcBuilderUtils,
} = modules.pcBuilder;

const ImageWrapper = styled(Box)(({ theme }) => ({
  textAlign: "center",
  position: "relative",
  display: "inline-block",
}));

const RowComponent: FC<Props> = ({ data }) => {
  const dispatch = useAppDispatch();
  const { push } = useRouter();
  const isMobile = useIsMobile();

  const buildPart = useAppSelector(
    pcBuilderSelectors.selectBuildPartFactory(data?.name)
  );
  const isAddComponentDisabled = useAppSelector(
    pcBuilderSelectors.selectIsAddComponentDisabledFactory(data?.name)
  );
  const componentNote = useMemo(
    () => pcBuilderUtils.getComponentNoteFactory(data?.name),
    [data?.name]
  );

  const addQuantity = () => {
    dispatch(
      pcBuilderActions.addQuantity({ product_id: buildPart?.product_id })
    );
  };

  const subtractQuantity = () => {
    dispatch(
      pcBuilderActions.subtractQuantity({ product_id: buildPart?.product_id })
    );
  };

  const onClickAddOrEditComponent = () => {
    const partsCategory = pcBuilderUtils.mapComponentNameToCategoryName(
      data?.name
    );
    dispatch(pcBuilderActions.setPartsCategory(partsCategory));
    push({
      pathname: "./pcbuilder-search",
      query: {
        category: partsCategory,
      },
    });
  };

  const onClickDelete = () => {
    dispatch(
      pcBuilderActions.removeComponent({
        product_id: buildPart?.product_id,
        category_name: buildPart?.category_name,
      })
    );
  };

  const onClickDeleteMultipart = (index: number) => {
    dispatch(
      pcBuilderActions.removeComponentOnMultipart({
        index,
        category_name: buildPart?.category_name,
      })
    );
  };

  const addMultipleQuantity = (index: number) => {
    dispatch(
      pcBuilderActions.addQuantityMultiple({
        part: buildPart,
        componentIndex: index,
      })
    );
  };

  const subtractMultipleQuantity = (index: number) => {
    dispatch(
      pcBuilderActions.subtractQuantityMultiple({
        part: buildPart,
        componentIndex: index,
      })
    );
  };

  if (isMobile && data?.multiple && !isEmpty(buildPart?.multipleParts)) {
    return (
      <Box borderBottom="1px solid #C1CDD7">
        {buildPart?.multipleParts?.map((bp, i) => (
          <FlexBox
            key={`${bp?.product_id}${i}`}
            flex={1}
            flexDirection="column"
            py={2}
            px={1.6}
          >
            {bp ? (
              <FlexBox flexDirection="column">
                <FlexBox flex={1} py={1}>
                  <FlexBox
                    flexDirection="column"
                    alignSelf="flex-start"
                    alignItems="center"
                    justifyItems="center"
                    bgcolor={data?.name}
                    border="1px solid #333333"
                    borderRadius={1.6}
                    px={1}
                  >
                    <Span color="grey.900" fontSize={8} fontWeight={600}>
                      {data?.name}
                    </Span>
                  </FlexBox>
                  <ImageWrapper
                    width={46}
                    height={46}
                    ml={1}
                    alignItems="center"
                    justifyContent="center"
                    overflow="hidden"
                  >
                    <Link
                      passHref
                      href={productDetailsPageUrl(
                        bp?.product_id,
                        bp?.product_uuid
                      )}
                      key={`${bp?.product_id}`}
                    >
                      <a>
                        <LazyImage
                          src={
                            bp?.image_url || "/assets/images/sad_red_hair.png"
                          }
                          layout="fill"
                          objectFit="contain"
                        />
                      </a>
                    </Link>
                  </ImageWrapper>
                  <FlexBox flexDirection="column" flex={2} pl={1.6}>
                    {bp?.retail_price ? (
                      <Link
                        passHref
                        href={productDetailsPageUrl(
                          bp?.product_id,
                          bp?.product_uuid
                        )}
                      >
                        <a>
                          <>
                            <H4 color="grey.900" fontSize={14} fontWeight="700">
                              {formatCurrency(bp?.retail_price * bp?.quantity)}
                            </H4>
                            <Span
                              color="grey.700"
                              fontSize={10}
                              sx={{ textDecoration: "line-through" }}
                            >
                              {formatCurrencySRP(
                                bp?.retail_price * bp?.quantity
                              )}
                            </Span>
                          </>
                        </a>
                      </Link>
                    ) : null}
                  </FlexBox>
                  <FlexBox flexDirection="column" flex={1}>
                    {bp?.quantity && !data?.disableQty ? (
                      <FlexBox alignItems="center">
                        <Button
                          color="primary"
                          sx={{ p: "0px" }}
                          variant="outlined"
                          disabled={bp?.quantity === 1}
                          onClick={() => subtractMultipleQuantity(i)}
                        >
                          <Remove fontSize="small" />
                        </Button>

                        <Span mx={1} fontWeight={600} fontSize={15}>
                          {bp?.quantity}
                        </Span>
                        <Button
                          color="primary"
                          sx={{ p: "0px" }}
                          variant="outlined"
                          disabled={bp?.quantity >= (bp?.stocks_left || 999)}
                          onClick={() => addMultipleQuantity(i)}
                        >
                          <Add fontSize="small" />
                        </Button>
                      </FlexBox>
                    ) : null}
                  </FlexBox>
                </FlexBox>
                <FlexBox flex={1}>
                  <BuildPartProperties
                    product_uuid={bp?.product_uuid}
                    custom_specs={bp?.custom_specs}
                    product_name={bp?.product_name}
                    parts_properties={bp?.parts_properties}
                    productSlug={bp?.product_id}
                  />
                </FlexBox>
              </FlexBox>
            ) : null}
            {bp ? null : (
              <FlexBox flex={1} flexDirection="column" alignItems="center">
                <Span color="grey.900" fontSize={18} fontWeight={700} mb={1.4}>
                  {data?.name}
                </Span>
                {componentNote ? (
                  <FlexBox justifyContent="center" mb={1.6}>
                    <H4
                      color="grey.900"
                      fontWeight={700}
                      fontSize={12}
                      textAlign="center"
                    >
                      {"Note: "}
                      <Span fontWeight={500}>{componentNote}</Span>
                    </H4>
                  </FlexBox>
                ) : null}
              </FlexBox>
            )}

            {bp ? (
              <FlexBox flex={1} mt={1} py={1}>
                <Button
                  color="primary"
                  variant="outlined"
                  style={{
                    borderColor: "#FF6464",
                    color: "#CF151A",
                    borderRadius: 4,
                    borderWidth: 1,
                    width: "100%",
                    height: 30,
                    marginRight: 8,
                  }}
                  startIcon={<DeleteOutlinedIcon />}
                  onClick={() => onClickDeleteMultipart(i)}
                >
                  {"REMOVE"}
                </Button>
              </FlexBox>
            ) : null}
          </FlexBox>
        ))}
        <FlexBox
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pb: 2,
          }}
        >
          <Button
            disabled={isAddComponentDisabled}
            color="secondary"
            variant="contained"
            style={{
              color: "white",
              fontSize: 12,
              borderRadius: 4,
              width: "100%",
              height: 30,
              marginLeft: 16,
              marginRight: 16,
            }}
            sx={{ bgcolor: colors.primary }}
            startIcon={<Icon style={{ color: "white" }}>add</Icon>}
            onClick={onClickAddOrEditComponent}
          >
            {`ADD ANOTHER [ ${data?.name} ]`}
          </Button>
        </FlexBox>
      </Box>
    );
  }

  if (isMobile) {
    return (
      <FlexBox
        flex={1}
        flexDirection="column"
        py={2}
        px={1.6}
        borderBottom="1px solid #C1CDD7"
      >
        {buildPart ? (
          <FlexBox flexDirection="column">
            <FlexBox flex={1} py={1}>
              <FlexBox
                flexDirection="column"
                alignSelf="flex-start"
                alignItems="center"
                justifyItems="center"
                bgcolor={data?.name}
                border="1px solid #333333"
                borderRadius={1.6}
                px={1}
              >
                <Span color="grey.900" fontSize={8} fontWeight={600}>
                  {data?.name}
                </Span>
              </FlexBox>
              <ImageWrapper
                width={46}
                height={46}
                ml={1}
                alignItems="center"
                justifyContent="center"
                overflow="hidden"
              >
                <Link
                  passHref
                  href={productDetailsPageUrl(
                    buildPart?.product_id,
                    buildPart?.product_uuid
                  )}
                  key={`${buildPart?.product_id}`}
                >
                  <a>
                    <LazyImage
                      src={
                        buildPart?.image_url ||
                        "/assets/images/sad_red_hair.png"
                      }
                      layout="fill"
                      objectFit="contain"
                    />
                  </a>
                </Link>
              </ImageWrapper>
              <FlexBox flexDirection="column" flex={2} pl={1.6}>
                {buildPart?.retail_price ? (
                  <Link
                    passHref
                    href={productDetailsPageUrl(
                      buildPart?.product_id,
                      buildPart?.product_uuid
                    )}
                  >
                    <a>
                      <>
                        <H4 color="grey.900" fontSize={14} fontWeight="700">
                          {formatCurrency(
                            buildPart?.retail_price * buildPart?.quantity
                          )}
                        </H4>
                        <Span
                          color="grey.700"
                          fontSize={10}
                          sx={{ textDecoration: "line-through" }}
                        >
                          {formatCurrencySRP(
                            buildPart?.retail_price * buildPart?.quantity
                          )}
                        </Span>
                      </>
                    </a>
                  </Link>
                ) : null}
              </FlexBox>
              <FlexBox flexDirection="column" flex={1}>
                {buildPart?.quantity && !data?.disableQty ? (
                  <FlexBox alignItems="center">
                    <Button
                      color="primary"
                      sx={{ p: "0px" }}
                      variant="outlined"
                      disabled={buildPart?.quantity === 1}
                      onClick={subtractQuantity}
                    >
                      <Remove fontSize="small" />
                    </Button>

                    <Span mx={1} fontWeight={600} fontSize={15}>
                      {buildPart?.quantity}
                    </Span>
                    <Button
                      color="primary"
                      sx={{ p: "0px" }}
                      variant="outlined"
                      disabled={
                        buildPart?.quantity >= (buildPart?.stocks_left || 999)
                      }
                      onClick={addQuantity}
                    >
                      <Add fontSize="small" />
                    </Button>
                  </FlexBox>
                ) : null}
              </FlexBox>
            </FlexBox>
            <FlexBox flex={1}>
              <BuildPartProperties
                product_uuid={buildPart?.product_uuid}
                custom_specs={buildPart?.custom_specs}
                product_name={buildPart?.product_name}
                parts_properties={buildPart?.parts_properties}
                productSlug={buildPart?.product_id}
              />
            </FlexBox>
          </FlexBox>
        ) : null}
        {buildPart ? null : (
          <FlexBox flex={1} flexDirection="column" alignItems="center">
            <Span color="grey.900" fontSize={18} fontWeight={700} mb={1.4}>
              {data?.name}
            </Span>
            {componentNote ? (
              <FlexBox justifyContent="center" mb={1.6}>
                <H4
                  color="grey.900"
                  fontWeight={700}
                  fontSize={12}
                  textAlign="center"
                >
                  {"Note: "}
                  <Span fontWeight={500}>{componentNote}</Span>
                </H4>
              </FlexBox>
            ) : null}
            <Button
              disabled={isAddComponentDisabled}
              color="secondary"
              variant="contained"
              style={{
                color: "white",
                fontSize: 12,
                borderRadius: 4,
                width: "100%",
                height: 30,
                marginLeft: 16,
                marginRight: 16,
              }}
              sx={{ bgcolor: colors.primary }}
              startIcon={<Icon style={{ color: "white" }}>add</Icon>}
              onClick={onClickAddOrEditComponent}
            >
              {"ADD COMPONENT"}
            </Button>
          </FlexBox>
        )}

        {buildPart ? (
          <FlexBox flex={1} mt={1} py={1}>
            <Button
              color="primary"
              variant="outlined"
              style={{
                borderColor: "#FF6464",
                color: "#CF151A",
                borderRadius: 4,
                borderWidth: 2,
                width: "100%",
                height: 30,
                marginRight: 8,
              }}
              startIcon={<DeleteOutlinedIcon />}
              onClick={onClickDelete}
            >
              {"REMOVE"}
            </Button>
            <Button
              color="secondary"
              variant="contained"
              style={{
                color: "white",
                borderRadius: 4,
                width: "100%",
                height: 30,
                marginLeft: 8,
              }}
              sx={{ bgcolor: colors.primary }}
              startIcon={<EditOutlinedIcon />}
              onClick={onClickAddOrEditComponent}
            >
              {"EDIT"}
            </Button>
          </FlexBox>
        ) : null}
      </FlexBox>
    );
  }

  if (data?.multiple && !isEmpty(buildPart?.multipleParts)) {
    return (
      <Box borderBottom="1px solid #C1CDD7">
        {buildPart?.multipleParts?.map((bp, i) => (
          <FlexRow flex={1} py={2} key={`${bp?.product_id}${i}`}>
            <FlexBox flex={1} alignItems="center" justifyContent="center">
              <FlexBox flexDirection="column" alignItems="center">
                {mapCategoryIcon(data?.name)}
                <FlexBox
                  width={100}
                  mt={1}
                  py={0}
                  px={2}
                  bgcolor={data?.name}
                  alignItems="center"
                  justifyContent="center"
                  border="1px solid #333333"
                  borderRadius={2}
                >
                  <Span color="grey.900" fontWeight={600} fontSize={10}>
                    {data?.name}
                  </Span>
                </FlexBox>
              </FlexBox>
            </FlexBox>

            <FlexBox flex={1.6} alignItems="center" justifyContent="center">
              {bp ? (
                <FlexBox
                  flex={1.6}
                  alignItems="center"
                  justifyContent="center"
                  flexDirection={"column"}
                >
                  <ImageWrapper
                    width={200}
                    height={200}
                    alignItems="center"
                    justifyContent="center"
                    overflow="hidden"
                  >
                    <Link
                      passHref
                      href={productDetailsPageUrl(
                        buildPart?.product_id,
                        buildPart?.product_uuid
                      )}
                      key={`${bp?.product_id}${bp?.product_uuid}`}
                    >
                      <a>
                        <LazyImage
                          src={
                            bp?.image_url || "/assets/images/sad_red_hair.png"
                          }
                          layout="fill"
                          objectFit="contain"
                        />
                      </a>
                    </Link>
                  </ImageWrapper>
                </FlexBox>
              ) : null}
            </FlexBox>
            {bp ? (
              <>
                <FlexBox flex={2}>
                  <BuildPartProperties
                    product_uuid={bp?.product_uuid}
                    custom_specs={bp?.custom_specs}
                    product_name={bp?.product_name}
                    parts_properties={bp?.parts_properties}
                    productSlug={bp.product_id}
                  />
                </FlexBox>
                <FlexBox flex={2}>
                  <FlexBox flexDirection="column" flex={2}>
                    {bp?.retail_price ? (
                      <Link
                        passHref
                        href={productDetailsPageUrl(
                          bp?.product_id,
                          bp?.product_uuid
                        )}
                      >
                        <a>
                          <>
                            <H4 color="grey.900" fontSize={20} fontWeight="700">
                              {formatCurrency(bp?.retail_price * bp?.quantity)}
                            </H4>
                            <Span
                              color="grey.700"
                              sx={{ textDecoration: "line-through" }}
                            >
                              {formatCurrencySRP(
                                bp?.retail_price * bp?.quantity
                              )}
                            </Span>
                          </>
                        </a>
                      </Link>
                    ) : null}
                  </FlexBox>
                  <FlexBox flexDirection="column" flex={1}>
                    {bp?.quantity && !data?.disableQty ? (
                      <FlexBox alignItems="center" mt={1} mr={2}>
                        <Button
                          color="primary"
                          sx={{ p: "5px" }}
                          variant="outlined"
                          disabled={bp?.quantity === 1}
                          onClick={() => subtractMultipleQuantity(i)}
                        >
                          <Remove fontSize="small" />
                        </Button>

                        <Span mx={1} fontWeight={600} fontSize={15}>
                          {bp?.quantity}
                        </Span>
                        <Button
                          color="primary"
                          sx={{ p: "5px" }}
                          variant="outlined"
                          onClick={() => addMultipleQuantity(i)}
                          disabled={bp?.quantity >= (bp?.stocks_left || 999)}
                        >
                          <Add fontSize="small" />
                        </Button>
                      </FlexBox>
                    ) : null}
                  </FlexBox>
                  <FlexBox flex={1} flexDirection="column">
                    <IconButton
                      color="primary"
                      onClick={() => onClickDeleteMultipart(i)}
                    >
                      <DeleteOutlinedIcon />
                    </IconButton>
                  </FlexBox>
                </FlexBox>
              </>
            ) : (
              <FlexBox flex={4} alignItems="center">
                {componentNote ? (
                  <H4 color="grey.900" fontWeight={700} fontSize={14}>
                    {"Note: "}
                    <Span fontWeight={500}>{componentNote}</Span>
                  </H4>
                ) : null}
              </FlexBox>
            )}
          </FlexRow>
        ))}
        <FlexBox
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pb: 2,
          }}
        >
          <Button
            disabled={isAddComponentDisabled}
            color="secondary"
            variant="contained"
            style={{ color: "white", borderRadius: 4, height: 40 }}
            sx={{ bgcolor: colors.primary }}
            size="medium"
            startIcon={<Icon style={{ color: "white" }}>add</Icon>}
            onClick={onClickAddOrEditComponent}
          >
            {`ADD ANOTHER [ ${data?.name} ]`}
          </Button>
        </FlexBox>
      </Box>
    );
  }

  return (
    <FlexRow flex={1} py={2} borderBottom="1px solid #C1CDD7">
      <FlexBox flex={1} alignItems="center" justifyContent="center">
        <FlexBox flexDirection="column" alignItems="center">
          {mapCategoryIcon(data?.name)}
          <FlexBox
            width={100}
            mt={1}
            py={0}
            px={2}
            bgcolor={data.bgColor}
            alignItems="center"
            justifyContent="center"
            border="1px solid #333333"
            borderRadius={2}
          >
            <Span color="grey.900" fontWeight={600} fontSize={10}>
              {data?.name}
            </Span>
          </FlexBox>
        </FlexBox>
      </FlexBox>

      <FlexBox flex={1.6} alignItems="center" justifyContent="center">
        {buildPart ? (
          <FlexBox
            flex={1.6}
            alignItems="center"
            justifyContent="center"
            flexDirection={"column"}
          >
            <ImageWrapper
              width={200}
              height={200}
              alignItems="center"
              justifyContent="center"
              overflow="hidden"
            >
              <Link
                passHref
                href={productDetailsPageUrl(
                  buildPart?.product_id,
                  buildPart?.product_uuid
                )}
                key={`${buildPart?.product_id}${buildPart?.product_uuid}`}
              >
                <a>
                  <LazyImage
                    src={
                      buildPart?.image_url || "/assets/images/sad_red_hair.png"
                    }
                    layout="fill"
                    objectFit="contain"
                  />
                </a>
              </Link>
            </ImageWrapper>
          </FlexBox>
        ) : (
          <Button
            disabled={isAddComponentDisabled}
            color="secondary"
            variant="contained"
            style={{ color: "white", borderRadius: 4, height: 40 }}
            sx={{ bgcolor: colors.primary }}
            size="medium"
            startIcon={<Icon style={{ color: "white" }}>add</Icon>}
            onClick={onClickAddOrEditComponent}
          >
            {"ADD COMPONENT"}
          </Button>
        )}
      </FlexBox>

      {buildPart ? (
        <>
          <FlexBox flex={2}>
            <BuildPartProperties
              product_uuid={buildPart?.product_uuid}
              custom_specs={buildPart?.custom_specs}
              product_name={buildPart?.product_name}
              parts_properties={buildPart?.parts_properties}
              productSlug={buildPart?.product_id}
            />
          </FlexBox>
          <FlexBox flex={2}>
            <FlexBox flexDirection="column" flex={2}>
              {buildPart?.retail_price ? (
                <Link
                  passHref
                  href={productDetailsPageUrl(
                    buildPart?.product_id,
                    buildPart?.product_uuid
                  )}
                >
                  <a>
                    <>
                      <H4 color="grey.900" fontSize={20} fontWeight="700">
                        {formatCurrency(
                          buildPart?.retail_price * buildPart?.quantity
                        )}
                      </H4>
                      <Span
                        color="grey.700"
                        sx={{ textDecoration: "line-through" }}
                      >
                        {formatCurrencySRP(
                          buildPart?.retail_price * buildPart?.quantity
                        )}
                      </Span>
                    </>
                  </a>
                </Link>
              ) : null}
            </FlexBox>
            <FlexBox flexDirection="column" flex={1}>
              {buildPart?.quantity && !data?.disableQty ? (
                <FlexBox alignItems="center" mt={1} mr={2}>
                  <Button
                    color="primary"
                    sx={{ p: "5px" }}
                    variant="outlined"
                    disabled={buildPart?.quantity === 1}
                    onClick={subtractQuantity}
                  >
                    <Remove fontSize="small" />
                  </Button>

                  <Span mx={1} fontWeight={600} fontSize={15}>
                    {buildPart?.quantity}
                  </Span>
                  <Button
                    color="primary"
                    sx={{ p: "5px" }}
                    variant="outlined"
                    onClick={addQuantity}
                    disabled={
                      buildPart?.quantity >= (buildPart?.stocks_left || 999)
                    }
                  >
                    <Add fontSize="small" />
                  </Button>
                </FlexBox>
              ) : null}
            </FlexBox>
            <FlexBox flex={1} flexDirection="column">
              <IconButton color="secondary" onClick={onClickAddOrEditComponent}>
                <EditOutlinedIcon />
              </IconButton>
              <IconButton color="primary" onClick={onClickDelete}>
                <DeleteOutlinedIcon />
              </IconButton>
            </FlexBox>
          </FlexBox>
        </>
      ) : (
        <FlexBox flex={4} alignItems="center">
          {componentNote ? (
            <H4 color="grey.900" fontWeight={700} fontSize={14}>
              {"Note: "}
              <Span fontWeight={500}>{componentNote}</Span>
            </H4>
          ) : null}
        </FlexBox>
      )}
    </FlexRow>
  );
};

export default RowComponent;
